import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import {
  authTypes,
  getInternalServerConfig,
  getExternalServers,
  normalizeServerConfig,
  validateServerConfig,
  clearAllCachedTokens
} from '../utils/auth';

const createEmptyServer = () => ({
  name: '',
  baseUrl: '',
  authType: authTypes.JWT,
  token: '',
  tokenUrl: '',
  clientId: '',
  clientSecret: '',
  id: ''
});

const AUTH_MODE_OPTIONS = [
  { value: authTypes.JWT, label: 'JWT Token' },
  { value: authTypes.CLIENT_CREDENTIALS, label: 'Client Credentials' }
];

const AUTH_MODE_LABELS = {
  [authTypes.JWT]: 'JWT Token',
  [authTypes.CLIENT_CREDENTIALS]: 'Client Credentials'
};

const isConfigComplete = (config, requireName = false) => {
  const normalized = normalizeServerConfig(config);
  const errors = validateServerConfig(normalized, 'Server');
  if (requireName && !(config.name || '').trim()) {
    return false;
  }
  return errors.length === 0;
};

const Settings = () => {
  const [internalSettings, setInternalSettings] = useState(() => {
    const config = getInternalServerConfig();
    return {
      baseUrl: config.baseUrl,
      authType: config.authType,
      token: config.token,
      tokenUrl: config.tokenUrl,
      clientId: config.clientId,
      clientSecret: config.clientSecret
    };
  });

  const [servers, setServers] = useState(() => getExternalServers());

  const [openDialog, setOpenDialog] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [newServer, setNewServer] = useState(createEmptyServer);

  const [error, setError] = useState(null);
  const [dialogError, setDialogError] = useState(null);

  const handleInternalSave = () => {
    const normalized = normalizeServerConfig({
      id: '__internal__',
      name: 'Internal API',
      ...internalSettings
    });

    const validationErrors = validateServerConfig(normalized, 'Internal API');
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      return;
    }

    try {
      localStorage.setItem('internalApiConfig', JSON.stringify(normalized));
      localStorage.setItem('baseUrl', normalized.baseUrl);

      if (normalized.authType === authTypes.JWT) {
        localStorage.setItem('token', normalized.token);
      } else {
        localStorage.removeItem('token');
      }

      clearAllCachedTokens();
      setError(null);
      window.location.href = '/';
    } catch (err) {
      setError('Failed to save settings: ' + err.message);
    }
  };

  const handleServerSave = () => {
    const normalized = normalizeServerConfig(newServer);
    const validationErrors = validateServerConfig(normalized, 'Server');

    if (!(newServer.name || '').trim()) {
      validationErrors.unshift('Server name is required');
    }

    if (validationErrors.length > 0) {
      setDialogError(validationErrors.join('. '));
      return;
    }

    try {
      const serverToSave = {
        ...normalized,
        name: newServer.name.trim(),
        id: editingServer !== null ? newServer.id : Date.now().toString()
      };

      let updatedServers;
      if (editingServer !== null) {
        updatedServers = servers.map((server, index) =>
          index === editingServer ? serverToSave : server
        );
      } else {
        updatedServers = [...servers, serverToSave];
      }

      localStorage.setItem('externalServers', JSON.stringify(updatedServers));
      clearAllCachedTokens();
      setServers(updatedServers);
      handleCloseDialog();
    } catch (err) {
      setDialogError('Failed to save server: ' + err.message);
    }
  };

  const handleDeleteServer = (index) => {
    const updatedServers = servers.filter((_, i) => i !== index);
    localStorage.setItem('externalServers', JSON.stringify(updatedServers));
    clearAllCachedTokens();
    setServers(updatedServers);
  };

  const handleEditServer = (index) => {
    const server = normalizeServerConfig(servers[index]);
    setEditingServer(index);
    setNewServer({
      ...createEmptyServer(),
      ...server,
      name: servers[index].name || server.name,
      id: servers[index].id || server.id
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingServer(null);
    setNewServer(createEmptyServer());
    setDialogError(null);
  };

  const internalConfigValid = isConfigComplete(internalSettings);
  const dialogConfigValid = isConfigComplete(newServer, true);

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3
          }}
        >
          <SettingsIcon sx={{ fontSize: 40, color: '#1976d2' }} />
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
            Settings
          </Typography>
        </Box>
        <Divider />
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>Internal API Settings</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Base URL"
            value={internalSettings.baseUrl}
            onChange={(e) => setInternalSettings((prev) => ({
              ...prev,
              baseUrl: e.target.value
            }))}
            fullWidth
            helperText="The base URL of your ONE Record server"
          />

          <FormControl fullWidth>
            <InputLabel id="internal-auth-mode-label">Authentication</InputLabel>
            <Select
              labelId="internal-auth-mode-label"
              label="Authentication"
              value={internalSettings.authType}
              onChange={(e) => setInternalSettings((prev) => ({
                ...prev,
                authType: e.target.value
              }))}
            >
              {AUTH_MODE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {internalSettings.authType === authTypes.JWT ? (
            <TextField
              label="JWT Token"
              value={internalSettings.token}
              onChange={(e) => setInternalSettings((prev) => ({
                ...prev,
                token: e.target.value
              }))}
              fullWidth
              type="password"
              helperText="Static JWT token used for API authentication"
            />
          ) : (
            <>
              <TextField
                label="Token URL"
                value={internalSettings.tokenUrl}
                onChange={(e) => setInternalSettings((prev) => ({
                  ...prev,
                  tokenUrl: e.target.value
                }))}
                fullWidth
                helperText="OAuth2 token endpoint for client credentials"
              />
              <TextField
                label="Client ID"
                value={internalSettings.clientId}
                onChange={(e) => setInternalSettings((prev) => ({
                  ...prev,
                  clientId: e.target.value
                }))}
                fullWidth
              />
              <TextField
                label="Client Secret"
                value={internalSettings.clientSecret}
                onChange={(e) => setInternalSettings((prev) => ({
                  ...prev,
                  clientSecret: e.target.value
                }))}
                fullWidth
                type="password"
              />
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <Button
            variant="contained"
            onClick={handleInternalSave}
            disabled={!internalConfigValid}
          >
            Save Internal Settings
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3
          }}
        >
          <Typography variant="h6">External Servers</Typography>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setOpenDialog(true)}
          >
            Add Server
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Base URL</TableCell>
                <TableCell>Authentication</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {servers.map((server, index) => (
                <TableRow key={server.id || index}>
                  <TableCell>{server.name}</TableCell>
                  <TableCell>{server.baseUrl}</TableCell>
                  <TableCell>{AUTH_MODE_LABELS[server.authType] || 'JWT Token'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => handleEditServer(index)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteServer(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {servers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="textSecondary">
                      No external servers configured
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingServer !== null ? 'Edit Server' : 'Add New Server'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {dialogError && (
              <Alert severity="error">
                {dialogError}
              </Alert>
            )}
            <TextField
              label="Server Name"
              value={newServer.name}
              onChange={(e) => setNewServer((prev) => ({
                ...prev,
                name: e.target.value
              }))}
              fullWidth
              required
            />
            <TextField
              label="Base URL"
              value={newServer.baseUrl}
              onChange={(e) => setNewServer((prev) => ({
                ...prev,
                baseUrl: e.target.value
              }))}
              fullWidth
              required
            />

            <FormControl fullWidth>
              <InputLabel id="server-auth-mode-label">Authentication</InputLabel>
              <Select
                labelId="server-auth-mode-label"
                label="Authentication"
                value={newServer.authType}
                onChange={(e) => setNewServer((prev) => ({
                  ...prev,
                  authType: e.target.value
                }))}
              >
                {AUTH_MODE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {newServer.authType === authTypes.JWT ? (
              <TextField
                label="JWT Token"
                value={newServer.token}
                onChange={(e) => setNewServer((prev) => ({
                  ...prev,
                  token: e.target.value
                }))}
                fullWidth
                type="password"
                required
              />
            ) : (
              <>
                <TextField
                  label="Token URL"
                  value={newServer.tokenUrl}
                  onChange={(e) => setNewServer((prev) => ({
                    ...prev,
                    tokenUrl: e.target.value
                  }))}
                  fullWidth
                  required
                />
                <TextField
                  label="Client ID"
                  value={newServer.clientId}
                  onChange={(e) => setNewServer((prev) => ({
                    ...prev,
                    clientId: e.target.value
                  }))}
                  fullWidth
                  required
                />
                <TextField
                  label="Client Secret"
                  value={newServer.clientSecret}
                  onChange={(e) => setNewServer((prev) => ({
                    ...prev,
                    clientSecret: e.target.value
                  }))}
                  fullWidth
                  type="password"
                  required
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleServerSave}
            disabled={!dialogConfigValid}
          >
            {editingServer !== null ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
